import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    Download, ExternalLink, FileText, EyeOff,
    ChevronFirst, ChevronLast, ChevronLeft, ChevronRight,
    StickyNote, X, User, Columns2,
} from "lucide-react";
import CandidateVoteBar from "@/components/CandidateVoteBar";

/**
 * Resume previewer.
 *
 * Perf: embeddable formats (PDF/TXT) are rendered by pointing an <iframe>
 * directly at the backend URL. The browser streams the response and
 * progressively renders the PDF — perceived load time drops from
 * "download-then-show" to "first page visible almost immediately".
 *
 * The blob-download path is used only for non-embeddable formats
 * (.doc/.docx) so the Download button can still hand off a real file.
 *
 * In screening mode we also background-prefetch the neighbouring resumes
 * so clicking Next/Prev feels instant on the HTTP cache's second hit.
 *
 * Pass either:
 *   - candidate (with id + resume_filename) for the CURRENT resume
 *   - candidateId + versionId for an archived version
 *
 * Screening mode (optional):
 *   - entries        : [application rows] in the currently visible filtered/sorted order
 *   - entryIndex     : current index into `entries`
 *   - onNavigate(i)  : parent handler to switch siblings
 *   - stages         : [{key,label}] pipeline stages for the stage dropdown
 *   - onStageChange  : (applicationId, stageKey) => void
 */
export default function ResumePreviewDialog({
    open, onClose, candidate: candidateProp, candidateId, versionId, filename,
    entries, entryIndex, onNavigate,
    stages = [], onStageChange,
    onOpenProfile,
}) {
    const hasList = Array.isArray(entries) && entries.length > 0 && typeof entryIndex === "number";
    const currentEntry = hasList ? entries[entryIndex] : null;
    const candidate = hasList ? currentEntry?.candidate : candidateProp;
    const cid = candidate?.id || candidateId;
    const vid = versionId || "current";
    const total = hasList ? entries.length : 0;
    const applicationId = currentEntry?.id || null;

    const fname = filename || candidate?.resume_filename || "resume";
    const ext = (fname.split(".").pop() || "").toLowerCase();
    // Cheap format classification — avoids the expensive content-type sniff.
    // The backend sends inline Content-Disposition so the browser renders
    // these directly in the iframe.
    const isEmbeddable = ext === "pdf" || ext === "txt";
    const isKnownDownloadOnly = ["doc", "docx", "rtf", "odt"].includes(ext);

    // Direct URL served by the backend (cookie-authenticated via withCredentials).
    // PDF fragment (`#navpanes=0`) collapses Chrome's native thumbnails sidebar
    // so the resume gets the full iframe width by default. Users can still
    // re-open the sidebar from the PDF viewer's own toolbar.
    const srcUrl = useMemo(() => {
        if (!cid) return null;
        const base = vid === "current"
            ? `${API}/candidates/${cid}/resume`
            : `${API}/candidates/${cid}/resume-versions/${vid}/download`;
        return ext === "pdf" ? `${base}#navpanes=0&toolbar=1&view=FitH` : base;
    }, [cid, vid, ext]);

    // Blob fallback only when we cannot embed and want a tidy Download link.
    // For PDFs we skip the fetch entirely — iframe streams from the server.
    const [blobUrl, setBlobUrl] = useState(null);
    const [blobLoading, setBlobLoading] = useState(false);
    const [blobErr, setBlobErr] = useState(null);
    // Track iframe render state so we can swap in a skeleton until the first paint.
    const [iframeLoaded, setIframeLoaded] = useState(false);

    // Inline "Add Note" state
    const [noteOpen, setNoteOpen] = useState(false);
    const [noteBody, setNoteBody] = useState("");
    const [noteBusy, setNoteBusy] = useState(false);

    // Side-by-side profile pane state.
    // `splitPct` = width of the LEFT (resume) pane as a percentage of the
    // dialog body. Drag the middle handle to resize.
    const [splitView, setSplitView] = useState(false);
    const [splitPct, setSplitPct] = useState(60);
    const bodyRef = useRef(null);
    const draggingRef = useRef(false);

    const startDrag = (e) => {
        e.preventDefault();
        draggingRef.current = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    };
    useEffect(() => {
        const onMove = (e) => {
            if (!draggingRef.current || !bodyRef.current) return;
            const rect = bodyRef.current.getBoundingClientRect();
            const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
            const pct = Math.max(20, Math.min(85, (x / rect.width) * 100));
            setSplitPct(pct);
        };
        const onUp = () => {
            if (!draggingRef.current) return;
            draggingRef.current = false;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchmove", onMove, { passive: false });
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, []);

    // Reset per-candidate state on open / navigate.
    useEffect(() => {
        if (!open) return;
        setIframeLoaded(false);
        setNoteOpen(false); setNoteBody("");
        setBlobUrl(null); setBlobErr(null);
    }, [open, cid, vid]);

    // Fetch blob only for the download-card fallback (non-embeddable formats).
    useEffect(() => {
        if (!open || !cid) return;
        if (isEmbeddable) return;              // Fast path — nothing to download
        let cancelled = false;
        let urlRef = null;
        setBlobLoading(true); setBlobErr(null);
        (async () => {
            try {
                const url = vid === "current"
                    ? `/candidates/${cid}/resume`
                    : `/candidates/${cid}/resume-versions/${vid}/download`;
                const res = await api.get(url, { responseType: "blob" });
                if (cancelled) return;
                const ct = res.headers["content-type"] || "application/octet-stream";
                urlRef = URL.createObjectURL(new Blob([res.data], { type: ct }));
                setBlobUrl(urlRef);
            } catch (e) {
                if (!cancelled) setBlobErr(formatApiError(e));
            } finally {
                if (!cancelled) setBlobLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (urlRef) URL.revokeObjectURL(urlRef);
        };
    }, [open, cid, vid, isEmbeddable]);

    // Prefetch the next & previous resumes so Prev/Next feels instant.
    // Uses the browser's HTTP cache — same URL that the iframe will request.
    const prefetchedRef = useRef(new Set());
    useEffect(() => {
        if (!open || !hasList) return;
        const neighbours = [entryIndex - 1, entryIndex + 1]
            .filter((i) => i >= 0 && i < total)
            .map((i) => entries[i]?.candidate)
            .filter((c) => c && c.id && c.resume_filename);
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 150));
        const handle = idle(() => {
            for (const c of neighbours) {
                const url = `${API}/candidates/${c.id}/resume`;
                if (prefetchedRef.current.has(url)) continue;
                prefetchedRef.current.add(url);
                // Low-priority GET that only warms the HTTP cache. We discard the body.
                try {
                    fetch(url, {
                        credentials: "include",
                        cache: "force-cache",
                        priority: "low",
                    }).then((r) => r.body?.cancel?.()).catch(() => {});
                } catch { /* ignore */ }
            }
        });
        return () => {
            if (window.cancelIdleCallback && typeof handle === "number") {
                try { window.cancelIdleCallback(handle); } catch { /* ignore */ }
            }
        };
    }, [open, hasList, entryIndex, total, entries]);

    // Keyboard shortcuts: Left/Right arrows cycle siblings.
    useEffect(() => {
        if (!open || !hasList) return;
        const onKey = (e) => {
            const t = e.target;
            const tag = (t?.tagName || "").toUpperCase();
            if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
            if (e.key === "ArrowRight" && entryIndex < total - 1) {
                e.preventDefault(); onNavigate?.(entryIndex + 1);
            } else if (e.key === "ArrowLeft" && entryIndex > 0) {
                e.preventDefault(); onNavigate?.(entryIndex - 1);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, hasList, entryIndex, total, onNavigate]);

    const atFirst = hasList && entryIndex <= 0;
    const atLast = hasList && entryIndex >= total - 1;

    const go = (i) => {
        if (!hasList) return;
        if (i < 0 || i > total - 1) return;
        if (i === entryIndex) return;
        onNavigate?.(i);
    };

    const submitNote = async () => {
        const body = noteBody.trim();
        if (!body || !cid) return;
        setNoteBusy(true);
        try {
            await api.post(`/candidates/${cid}/notes`, { body, pinned: false });
            toast.success("Note added");
            setNoteBody("");
            setNoteOpen(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setNoteBusy(false);
        }
    };

    const navBtn = "w-8 h-8 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed";

    // Download/Open hrefs: use direct URL for the embeddable case (always
    // available instantly), fall back to the pre-fetched blob otherwise.
    const openHref = isEmbeddable ? srcUrl : (blobUrl || srcUrl);
    const downloadHref = isEmbeddable ? srcUrl : (blobUrl || srcUrl);

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent
                className="rounded-none !max-w-[95vw] w-[95vw] p-0 gap-0 h-[95vh] flex flex-col"
                data-testid="resume-preview-dialog"
            >
                <DialogHeader className="px-4 sm:px-5 pt-3 pb-2 border-b border-border space-y-2">
                    {/* Row 1 — Title + nav + counter + close */}
                    <div className="flex items-center gap-3">
                        <DialogTitle className="font-display font-bold text-base flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="w-4 h-4 text-ikb shrink-0" />
                            <span className="truncate" title={candidate?.name || fname}>
                                {candidate?.name || fname}
                            </span>
                            {candidate?.name && (
                                <span className="hidden md:inline text-xs font-mono text-muted-foreground truncate" title={fname}>
                                    · {fname}
                                </span>
                            )}
                        </DialogTitle>

                        {/* Screening-mode navigator */}
                        {hasList && (
                            <div className="flex items-center gap-1 shrink-0" data-testid="resume-preview-nav">
                                <button
                                    type="button"
                                    onClick={() => go(0)}
                                    disabled={atFirst}
                                    className={navBtn}
                                    title="First candidate"
                                    data-testid="resume-preview-first"
                                    aria-label="First candidate"
                                >
                                    <ChevronFirst className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => go(entryIndex - 1)}
                                    disabled={atFirst}
                                    className={navBtn}
                                    title="Previous candidate (←)"
                                    data-testid="resume-preview-prev"
                                    aria-label="Previous candidate"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <span
                                    className="px-2 h-8 inline-flex items-center text-[11px] font-mono tabular-nums border border-border bg-secondary/40 min-w-[60px] justify-center"
                                    data-testid="resume-preview-counter"
                                    title="Current candidate position in the list"
                                >
                                    {entryIndex + 1} / {total}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => go(entryIndex + 1)}
                                    disabled={atLast}
                                    className={navBtn}
                                    title="Next candidate (→)"
                                    data-testid="resume-preview-next"
                                    aria-label="Next candidate"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => go(total - 1)}
                                    disabled={atLast}
                                    className={navBtn}
                                    title="Last candidate"
                                    data-testid="resume-preview-last"
                                    aria-label="Last candidate"
                                >
                                    <ChevronLast className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="w-8 h-8 inline-flex items-center justify-center border border-border bg-white hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 shrink-0"
                            data-testid="resume-preview-close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Row 2 — Screening controls + file actions */}
                    <div className="flex items-center flex-wrap gap-2">
                        {hasList && stages?.length > 0 && applicationId && (
                            <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                                Stage
                                <select
                                    value={currentEntry?.stage || "Applied"}
                                    onChange={(e) => onStageChange?.(applicationId, e.target.value)}
                                    className="h-8 px-2 border border-border bg-white text-xs font-sans normal-case tracking-normal"
                                    data-testid="resume-preview-stage"
                                >
                                    {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                                </select>
                            </label>
                        )}

                        {applicationId && (
                            <div data-testid="resume-preview-votes">
                                <CandidateVoteBar
                                    applicationId={applicationId}
                                    initial={currentEntry?.vote_summary}
                                    size="sm"
                                />
                            </div>
                        )}

                        {cid && (
                            <button
                                type="button"
                                onClick={() => setNoteOpen((v) => !v)}
                                className={`text-xs font-mono uppercase tracking-widest border px-2 py-1.5 inline-flex items-center gap-1 ${
                                    noteOpen
                                        ? "border-ikb bg-ikb text-white"
                                        : "border-border bg-white hover:bg-secondary"
                                }`}
                                data-testid="resume-preview-add-note"
                                title="Add a note to this candidate"
                            >
                                <StickyNote className="w-3 h-3" />
                                Add Note
                            </button>
                        )}

                        {cid && (
                            <button
                                type="button"
                                onClick={() => setSplitView((v) => !v)}
                                className={`text-xs font-mono uppercase tracking-widest border px-2 py-1.5 inline-flex items-center gap-1 ${
                                    splitView
                                        ? "border-ikb bg-ikb text-white"
                                        : "border-border bg-white hover:bg-secondary"
                                }`}
                                data-testid="resume-preview-split-toggle"
                                title={splitView ? "Hide profile pane" : "Show candidate profile side-by-side"}
                            >
                                <Columns2 className="w-3 h-3" />
                                {splitView ? "Hide profile" : "Profile pane"}
                            </button>
                        )}

                        {cid && onOpenProfile && !splitView && (
                            <button
                                type="button"
                                onClick={() => {
                                    onOpenProfile({
                                        candidateId: cid,
                                        applicationId,
                                        candidate,
                                        entry: currentEntry,
                                    });
                                }}
                                className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-1"
                                data-testid="resume-preview-open-profile"
                                title="Open full candidate profile in a dialog"
                            >
                                <User className="w-3 h-3" />
                                View Profile
                            </button>
                        )}

                        <div className="flex items-center gap-2 ml-auto">
                            <a
                                href={`${API}/candidates/${cid}/masked-resume`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="resume-preview-masked"
                                title="Open masked version (contact info, social links, surname redacted) — Print → Save as PDF"
                                className="text-xs font-mono uppercase tracking-widest border border-amber-400 bg-amber-50 text-amber-800 px-2 py-1.5 hover:bg-amber-100 inline-flex items-center gap-1"
                            >
                                <EyeOff className="w-3 h-3" /> Masked
                            </a>
                            <a
                                href={openHref || "#"}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="resume-preview-open-tab"
                                className="text-xs font-mono uppercase tracking-widest border border-border px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-1"
                            >
                                <ExternalLink className="w-3 h-3" /> Open
                            </a>
                            <a
                                href={downloadHref || "#"}
                                download={fname}
                                data-testid="resume-preview-download"
                                className="text-xs font-mono uppercase tracking-widest border border-border px-2 py-1.5 hover:bg-secondary inline-flex items-center gap-1"
                            >
                                <Download className="w-3 h-3" /> Download
                            </a>
                        </div>
                    </div>

                    {noteOpen && (
                        <div className="border border-ikb/30 bg-ikb/5 p-2 space-y-2" data-testid="resume-preview-note-form">
                            <textarea
                                rows={3}
                                value={noteBody}
                                onChange={(e) => setNoteBody(e.target.value)}
                                placeholder={`Add a note about ${candidate?.name || "this candidate"}…`}
                                className="w-full text-sm px-2 py-1.5 border border-border bg-white resize-y"
                                autoFocus
                                data-testid="resume-preview-note-textarea"
                                onKeyDown={(e) => {
                                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                        e.preventDefault();
                                        submitNote();
                                    }
                                }}
                            />
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] font-mono text-muted-foreground">
                                    Tip: ⌘/Ctrl + Enter to save
                                </span>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { setNoteOpen(false); setNoteBody(""); }}
                                        className="text-xs font-mono uppercase tracking-widest border border-border bg-white px-2 py-1 hover:bg-secondary"
                                        data-testid="resume-preview-note-cancel"
                                    >
                                        Cancel
                                    </button>
                                    <Button
                                        type="button"
                                        onClick={submitNote}
                                        disabled={!noteBody.trim() || noteBusy}
                                        className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-7 px-3 text-xs"
                                        data-testid="resume-preview-note-save"
                                    >
                                        {noteBusy ? "Saving…" : "Save note"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogHeader>

                <div
                    ref={bodyRef}
                    className={`flex-1 bg-secondary overflow-hidden relative ${splitView ? "flex" : ""}`}
                >
                    <div
                        className={splitView ? "relative h-full" : "absolute inset-0"}
                        style={splitView ? { width: `${splitPct}%` } : undefined}
                    >
                    {isEmbeddable ? (
                        <>
                            {!iframeLoaded && (
                                <div className="absolute inset-0 flex items-center justify-center text-sm text-foreground font-mono bg-secondary">
                                    Loading resume…
                                </div>
                            )}
                            {srcUrl && (
                                <iframe
                                    // `key` forces a fresh iframe when cycling siblings, so onLoad fires again
                                    key={srcUrl}
                                    src={srcUrl}
                                    title={fname}
                                    className="w-full h-full bg-white"
                                    data-testid="resume-preview-iframe"
                                    onLoad={() => setIframeLoaded(true)}
                                    // Hint for the browser's PDF viewer to start parsing ASAP
                                    loading="eager"
                                />
                            )}
                        </>
                    ) : blobLoading ? (
                        <div className="h-full flex items-center justify-center text-sm text-foreground font-mono">Loading resume…</div>
                    ) : blobErr ? (
                        <div className="h-full flex items-center justify-center text-sm text-destructive">{blobErr}</div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center gap-4 p-8">
                            <FileText className="w-12 h-12 text-foreground/40" />
                            <h3 className="font-display font-bold text-xl">{(ext || "FILE").toUpperCase()} file</h3>
                            <p className="text-sm text-foreground/80 max-w-md">
                                {isKnownDownloadOnly
                                    ? "Browser preview isn't supported for Word documents. Open or download to view."
                                    : "Browser preview isn't supported for this format. Open or download to view."}
                            </p>
                            <div className="flex gap-2">
                                <a href={openHref || "#"} target="_blank" rel="noreferrer">
                                    <Button variant="outline" className="rounded-none"><ExternalLink className="w-4 h-4 mr-2" /> Open in new tab</Button>
                                </a>
                                <a href={downloadHref || "#"} download={fname}>
                                    <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white"><Download className="w-4 h-4 mr-2" /> Download</Button>
                                </a>
                            </div>
                        </div>
                    )}
                    </div>

                    {splitView && cid && (
                        <>
                            <div
                                onMouseDown={startDrag}
                                onTouchStart={startDrag}
                                className="w-1.5 bg-border hover:bg-ikb cursor-col-resize relative group shrink-0"
                                data-testid="resume-preview-split-handle"
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="Resize profile pane"
                                title="Drag to resize"
                            >
                                <div className="absolute inset-y-0 -left-1 -right-1" />
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-10 bg-white border border-border group-hover:border-ikb" />
                            </div>
                            <div
                                className="relative h-full bg-background"
                                style={{ width: `calc(${100 - splitPct}% - 6px)` }}
                            >
                                <iframe
                                    key={`profile-${cid}`}
                                    src={`/candidates/${cid}/embed`}
                                    title={`Candidate profile — ${candidate?.name || ""}`}
                                    className="w-full h-full bg-white"
                                    data-testid="resume-preview-profile-iframe"
                                />
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
